import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { PopupModal } from "react-calendly";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useSaleDiscounts, saleLabel } from "../lib/saleDiscount";

import Hero from "../components/Hero"; // Keep importing the Hero
import PopupOffer from "../components/PopupOffer"; // Keep if still needed
import ProductQuiz from "../components/ProductQuiz";
import Slider from "react-slick";

// Reworked HomeScreen Component
const HomeScreen = () => {
	const sale = useSaleDiscounts();
	const [openCalendly, setOpenCalendly] = useState(false);
	// State for PopupOffer (keep if you still use it for specific promotions)
	const [showPopupOffer, setShowPopupOffer] = useState(false);
	const [forcePopupOffer, setForcePopupOffer] = useState(false);
	// Testimonial slider is driven by our own prev/next buttons (slick's arrows are globally red)
	const testimonialSliderRef = useRef(null);

	// Product Categories - Make this static and visual
	const productCategories = [
		{
			title: "Honeycomb Cellular Shades",
			url: "/images/newHome/shades/cellular.jpg", // Use clear category images
			to: "/shop/info/simply-cellular",
			description: "Energy efficient & stylish insulation.",
		},
		{
			title: "Custom Roller Shades",
			url: "/images/newHome/shades/privacy_roller.jpg",
			to: "/shop/info/roller",
			description: "Sleek, modern light control.",
		},
		{
			title: "Zebra Roller Shades",
			url: "/images/newHome/shades/privacy_zebra.jpg",
			to: "/shop/info/zebra",
			description: "Dual layers for versatile privacy.",
		},
		{
			title: "Sheer Horizontal Shades",
			url: "/images/shadeinfo/shades/sheer/sheer.jpg",
			to: "/shop/info/sheer",
			description: "Softly filter light with elegance.",
		},
	];

	const testimonialSliderSettings = {
		dots: false, // Show navigation dots
		infinite: true, // Loop the slides
		speed: 500, // Transition speed
		slidesToShow: 3, // Number of slides visible at once on desktop
		slidesToScroll: 1, // How many slides to scroll at a time
		autoplay: true, // Enable autoplay
		autoplaySpeed: 2500, // Delay between slides
		pauseOnHover: true, // Pause autoplay on hover
		arrows: false, // Custom prev/next buttons sit beside the heading
		responsive: [
			// Adjust settings for different screen sizes
			{
				breakpoint: 1024, // Medium screens (tablets)
				settings: {
					slidesToShow: 2,
					slidesToScroll: 1,
				},
			},
			{
				breakpoint: 768, // Small screens (mobile)
				settings: {
					slidesToShow: 1,
					slidesToScroll: 1,
					arrows: false, // Hide arrows on mobile, rely on swipe/dots
				},
			},
		],
	};

	const testimonials = [
		{
			// Existing #1
			quote:
				"Absolutely love my new shades! The quality is top-notch and they fit perfectly. Highly recommend!",
			author: "Sarah M., Toronto ON",
			rating: 5,
		},
		{
			// Existing #2
			quote:
				"The free samples made choosing so easy. Installation was a breeze. Great value and service.",
			author: "David L., Vancouver BC",
			rating: 5,
		},
		{
			// Existing #3
			quote:
				"Fast shipping and excellent customer support. My living room looks completely transformed.",
			author: "Emily P., Calgary AB",
			rating: 5,
		},
		{
			// New #4 - Specific Product & Fit
			quote:
				"Ordered Zebra shades for my office. Measured exactly as the guide showed and the fit is flawless. They look incredibly modern.",
			author: "Michael B., Montreal QC",
			rating: 5,
		},
		{
			// New #5 - Quality & Value
			quote:
				"Was skeptical about ordering online, but the quality is fantastic, especially for the price. Better than big box stores!",
			author: "Jennifer S., Halifax NS",
			rating: 5,
		},
		{
			// New #6 - Ease of Installation
			quote:
				"Never installed blinds before, but the instructions were clear and simple. Had them up in under an hour.",
			author: "Chris T., Winnipeg MB",
			rating: 5, // Slightly different rating for variety
		},
		{
			// New #7 - Blackout Feature
			quote:
				"The blackout cellular shades are truly blackout! Perfect for my nursery. Finally, daytime naps are possible.",
			author: "Aisha K., Edmonton AB",
			rating: 5,
		},
		{
			// New #8 - Customer Service (Measurement Issue)
			quote:
				"I messed up my measurements slightly. Contacted support and they were incredibly helpful in getting it sorted out with minimal cost. Lifesavers!",
			author: "Ryan G., Ottawa ON",
			rating: 5,
		},
		{
			// New #9 - Speed
			quote:
				"Couldn't believe how fast they arrived! Custom made and shipped so quickly. Very impressed.",
			author: "Linda H., Saskatoon SK",
			rating: 5,
		},
		{
			// New #10 - Fabric Samples Accuracy
			quote:
				"The free samples matched the final product perfectly. Took the guesswork out of choosing the colour online.",
			author: "Kevin W., Mississauga ON",
			rating: 5,
		},
		{
			// New #11 - Sheer Shades Appearance
			quote:
				"My sheer shades are gorgeous! They let in so much soft light while still offering a touch of privacy during the day.",
			author: "Maria V., Quebec City QC",
			rating: 5,
		},
		{
			// New #12 - Repeat Customer
			quote:
				"This is my second order from Off Cut Shades. Consistent quality and service. Won't buy shades anywhere else.",
			author: "Peter J., Hamilton ON",
			rating: 5,
		},
		{
			// New #13 - Motorization
			quote:
				"Splurged on the motorized roller shades for the living room. So convenient and quiet! The app control is fantastic.",
			author: "Samantha D., Surrey BC",
			rating: 5,
		},
		{
			// New #14 - Website Experience
			quote:
				"The website customizer tool was easy to navigate and understand. Made ordering simple.",
			author: "Daniel C., London ON",
			rating: 5,
		},
		{
			// New #15 - Canadian Made Aspect
			quote:
				"Love that these are made in Canada! Happy to support local manufacturing while getting a great product.",
			author: "Chloe L., Victoria BC",
			rating: 5,
		},
		{
			// New #16 - Cellular Insulation
			quote:
				"Noticeable difference in room temperature since installing the cellular shades. They really help insulate.",
			author: "Mark R., St. John's NL",
			rating: 5,
		},
		{
			// New #17 - Zebra Functionality
			quote:
				"The versatility of the Zebra shades is amazing. Full privacy, filtered light, or open view - all in one.",
			author: "Isabelle F., Gatineau QC",
			rating: 5,
		},
		{
			// New #18 - Value Proposition
			quote:
				"Excellent value for custom shades. Shopped around and Off Cut had the best combination of price, quality, and speed.",
			author: "Ben P., Brampton ON",
			rating: 5,
		},
		{
			// New #19 - Shipping Packaging
			quote:
				"Shades arrived well-packaged and undamaged. Appreciated the careful shipping.",
			author: "Olivia N., Regina SK",
			rating: 5,
		},
		{
			// New #20 - Specific Need (Office Glare)
			quote:
				"Got the Solar shades for my home office to cut down on glare. They work perfectly and I can still see outside.",
			author: "Noah A., Vaughan ON",
			rating: 5,
		},
		{
			// New #21 - Color Choice Satisfaction
			quote:
				"The 'Charcoal' blackout fabric was exactly the dark grey I was looking for. Samples were spot on.",
			author: "Grace B., Kitchener ON",
			rating: 5,
		},
		{
			// New #22 - Ease for Large Windows
			quote:
				"Ordered a very wide shade for my patio door. The customization process handled the large size perfectly, and it operates smoothly.",
			author: "Ethan Z., Burnaby BC",
			rating: 5,
		},
		{
			// New #23 - Comparing to Professionals
			quote:
				"Felt like I got professionally fitted custom shades without the huge markup. Saved a lot doing it myself with their guides.",
			author: "Sophia T., Laval QC",
			rating: 5,
		},
		{
			// New #24 - Minor Issue Resolved
			quote:
				"Had a small issue with one bracket, customer service sent a replacement immediately. Great support.",
			author: "Liam G., Markham ON",
			rating: 5,
		},
		{
			// New #25 - Aesthetics Focus
			quote:
				"The clean lines of the roller shades updated my space instantly. Very happy with the minimalist look.",
			author: "Ava H., Halifax NS",
			rating: 5,
		},
		{
			// New #26 - Trust Factor Overcoming Hesitation
			quote:
				"Was hesitant to buy custom online, but the guarantees and positive reviews convinced me. Glad I did!",
			author: "Jackson K., Windsor ON",
			rating: 5,
		},
		{
			// New #27 - Sheer + Privacy Balance
			quote:
				"Needed something for the dining room that wasn't too heavy. The light filtering sheer shades are perfect - bright but not exposed.",
			author: "Isla P., Saskatoon SK",
			rating: 5,
		},
		{
			// New #28 - Simplicity Focus
			quote:
				"Just wanted simple, functional roller shades without too many decisions. The 'Easy Pick' concept (if applicable) or basic options were perfect.",
			author: "Lucas M., Sherbrooke QC",
			rating: 5,
		},
		{
			// New #29 - Return Customer for Different Room
			quote:
				"Got blackout for the bedroom first, now back for solar shades for the living room. Consistently good experience.",
			author: "Mia W., Richmond BC",
			rating: 5,
		},
		{
			// New #30 - Overall Satisfaction
			quote:
				"From samples to ordering to fast delivery and easy installation - the whole process was smooth. Highly satisfied.",
			author: "William R., St. Catharines ON",
			rating: 5,
		},
	];

	// Animation variants for sections
	const sectionVariants = {
		hidden: { opacity: 0, y: 16 },
		visible: {
			opacity: 1,
			y: 0,
			transition: { duration: 0.35, ease: "easeOut" },
		},
	};

	return (
		<div className="overflow-x-hidden bg-[#FAFAF7]">
			<Helmet>
				{/* SEO Optimized Title & Description */}
				<title>{`Custom Window Shades Canada | ${saleLabel(sale)} Off | Off Cut Shades`}</title>
				<meta
					name="description"
					content={`Shop custom window blinds & shades online. Autumn Sale: ${Math.round(sale.global * 100)}% off${sale.fabric > 0 ? ` + an extra ${Math.round(sale.fabric * 100)}% off` : ""} Cellular, Roller, Zebra, Sheer & Dream Shades. Handcrafted in Toronto. Free Samples & Shipping!`}
				/>
				<link rel="canonical" href="https://offcutshades.com/" />
				{/* Add other relevant meta tags */}
			</Helmet>

			{/* Optional Popup Offer */}
			<PopupOffer
				show={showPopupOffer}
				setShow={setShowPopupOffer}
				force={forcePopupOffer}
				setForce={setForcePopupOffer}
			/>

			{/* 1. Hero Section */}
			<Hero />

			{/* Everything below the hero: Inter body, Instrument Serif headings */}
			<div className="font-inter text-[#141414]">
				{/* 2. Value strip */}
				<section className="max-w-[1320px] mx-auto px-4 sm:px-8 lg:px-14 pt-12 sm:pt-16">
					<div className="grid grid-cols-2 lg:grid-cols-4 border-y border-[#E4E2DC]">
						{[
							{
								title: "Proudly Canadian Made",
								description: "Handcrafted in our Toronto facility.",
							},
							{
								title: "Fast, Free Shipping",
								description: "Complimentary across Canada.",
							},
							{
								title: "Expert Support",
								description: "Chat, email or phone.",
							},
							{
								title: "Satisfaction Guarantee",
								description: "We stand by our quality.",
							},
						].map((feature) => (
							<div key={feature.title} className="py-5 sm:py-6 pr-4 lg:px-6 lg:first:pl-0">
								<h3 className="text-[15px] font-medium">{feature.title}</h3>
								<p className="text-sm text-[#5E5E5A] mt-1">{feature.description}</p>
							</div>
						))}
					</div>
				</section>

				{/* 3. Shop by style */}
				<motion.section
					className="max-w-[1320px] mx-auto px-4 sm:px-8 lg:px-14 pt-20 sm:pt-28"
					variants={sectionVariants}
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true }}
				>
					<div className="flex flex-wrap items-end justify-between gap-4 mb-8 sm:mb-10">
						<h2 className="font-instrument text-5xl md:text-[64px] font-normal tracking-[-0.01em] leading-[0.95]">
							Shop by style
						</h2>
						<Link
							to="/shop"
							className="group inline-flex items-center gap-2 border-b border-[#141414] pb-1 uppercase tracking-[0.2em] text-xs font-medium"
						>
							View all shades
							<ArrowRight
								aria-hidden="true"
								strokeWidth={1.5}
								className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1"
							/>
						</Link>
					</div>
					<div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-8 sm:gap-6">
						{productCategories.map((category, index) => (
							<Link key={category.title} to={category.to} className="group block">
								<div className="overflow-hidden bg-[#F1EFEA]">
									<img
										src={category.url}
										alt={category.title}
										className="w-full aspect-[3/4] object-cover transition-transform duration-700 group-hover:scale-[1.03]"
										loading="lazy"
									/>
								</div>
								<div className="flex items-baseline gap-3 mt-4">
									<span className="text-xs font-normal text-[#5E5E5A]">
										{String(index + 1).padStart(2, "0")}
									</span>
									<div>
										<h3 className="text-base sm:text-lg font-medium leading-snug">
											{category.title}
										</h3>
										<p className="text-sm text-[#5E5E5A] hidden sm:block">
											{category.description}
										</p>
									</div>
								</div>
							</Link>
						))}
					</div>
				</motion.section>

				{/* 4. Product quiz */}
				<motion.section
					className="pt-20 sm:pt-28"
					variants={sectionVariants}
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true }}
				>
					<div className="max-w-[1320px] mx-auto px-4 sm:px-8 lg:px-14 mb-8 sm:mb-10">
						<h2 className="font-instrument text-5xl md:text-[64px] font-normal tracking-[-0.01em] leading-[0.95]">
							Not sure where to start?
						</h2>
						<p className="mt-4 text-lg text-[#5E5E5A] max-w-xl">
							Answer a few quick questions, and we'll recommend the perfect shades
							for your needs.
						</p>
					</div>
					<div className="w-full mx-auto">
						<ProductQuiz embedded />
					</div>
				</motion.section>

				{/* 5. How it works */}
				<motion.section
					className="max-w-[1320px] mx-auto px-4 sm:px-8 lg:px-14 pt-20 sm:pt-28"
					variants={sectionVariants}
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true }}
				>
					<h2 className="font-instrument text-5xl md:text-[64px] font-normal tracking-[-0.01em] leading-[0.95] mb-10 sm:mb-12">
						How it works
					</h2>
					<div className="grid grid-cols-1 md:grid-cols-3 gap-10">
						{[
							{
								title: "Explore & sample",
								description:
									"Browse styles and order up to 5 free fabric samples to find your perfect match.",
							},
							{
								title: "Measure & customize",
								description:
									"Easily measure your windows (we help!) and customize your shades online.",
							},
							{
								title: "Order & install",
								description:
									"Place your order and receive your custom, Canadian-made shades quickly.",
							},
						].map((step, index) => (
							<div key={step.title} className="border-t border-[#141414] pt-6">
								<div className="font-instrument text-[56px] font-normal leading-none">{index + 1}</div>
								<h3 className="mt-5 mb-2 text-lg font-medium">{step.title}</h3>
								<p className="text-[#5E5E5A]">{step.description}</p>
							</div>
						))}
					</div>
					<Link
						to="/guides/measuring-guide"
						className="group mt-10 inline-flex items-center gap-2 border-b border-[#141414] pb-1 uppercase tracking-[0.2em] text-xs font-medium"
					>
						Read the measuring guide
						<ArrowRight
							aria-hidden="true"
							strokeWidth={1.5}
							className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1"
						/>
					</Link>
				</motion.section>

				{/* 6. Testimonials */}
				<motion.section
					className="max-w-[1320px] mx-auto px-4 sm:px-8 lg:px-14 pt-20 sm:pt-28"
					variants={sectionVariants}
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true }}
				>
					<div className="flex items-end justify-between gap-4 mb-10 sm:mb-12">
						<h2 className="font-instrument text-5xl md:text-[64px] font-normal tracking-[-0.01em] leading-[0.95]">
							In their words
						</h2>
						<div className="flex gap-2">
							<button
								type="button"
								onClick={() => testimonialSliderRef.current?.slickPrev()}
								aria-label="Previous reviews"
								className="w-11 h-11 flex items-center justify-center border border-[#141414] text-[#141414] hover:bg-[#141414] hover:text-[#FAFAF7] transition-colors duration-200"
							>
								<ArrowLeft size={16} strokeWidth={1.5} aria-hidden="true" />
							</button>
							<button
								type="button"
								onClick={() => testimonialSliderRef.current?.slickNext()}
								aria-label="Next reviews"
								className="w-11 h-11 flex items-center justify-center border border-[#141414] text-[#141414] hover:bg-[#141414] hover:text-[#FAFAF7] transition-colors duration-200"
							>
								<ArrowRight size={16} strokeWidth={1.5} aria-hidden="true" />
							</button>
						</div>
					</div>
					<div className="relative -mx-3 pb-6">
						<Slider ref={testimonialSliderRef} {...testimonialSliderSettings}>
							{testimonials.map((testimonial, index) => (
								<div key={index} className="px-3 h-full">
									<figure className="h-full flex flex-col border-t border-[#E4E2DC] pt-6">
										<blockquote className="font-instrument text-2xl sm:text-[26px] font-normal leading-[1.25] flex-grow">
											“{testimonial.quote}”
										</blockquote>
										<figcaption className="mt-6 text-sm text-[#5E5E5A]">
											{testimonial.author}
										</figcaption>
									</figure>
								</div>
							))}
						</Slider>
					</div>
				</motion.section>

				{/* 7. Free samples */}
				<motion.section
					className="max-w-[1320px] mx-auto px-4 sm:px-8 lg:px-14 pt-20 sm:pt-28"
					variants={sectionVariants}
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true }}
				>
					<div className="flex flex-col md:flex-row bg-[#F1EFEA]">
						<div className="md:w-1/2 p-8 sm:p-12 lg:p-16 flex flex-col justify-center">
							<h2 className="font-instrument text-[44px] md:text-[56px] font-normal tracking-[-0.01em] leading-[0.95]">
								See it in your light first.
							</h2>
							<p className="mt-5 text-lg text-[#5E5E5A] max-w-md">
								Not sure which color or material? Order up to 5 free samples
								delivered right to your door.
							</p>
							<div className="mt-8">
								<Link
									to="/swatches"
									className="group inline-flex items-center gap-2 border-b border-[#141414] pb-1 uppercase tracking-[0.2em] text-xs font-medium"
								>
									Request free samples
									<ArrowRight
										aria-hidden="true"
										strokeWidth={1.5}
										className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1"
									/>
								</Link>
							</div>
						</div>
						<img
							src="/images/kitchen.jpg"
							alt="Kitchen with custom roller shades"
							className="md:w-1/2 w-full h-72 md:h-[520px] object-cover"
							loading="lazy"
						/>
					</div>
				</motion.section>

				{/* 8. Expert advice */}
				<section className="max-w-[1320px] mx-auto px-4 sm:px-8 lg:px-14 py-20 sm:py-28">
					<div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 border-t border-[#E4E2DC] pt-12">
						<div>
							<h2 className="font-instrument text-[44px] md:text-[56px] font-normal tracking-[-0.01em] leading-[0.95]">
								Need expert advice?
							</h2>
							<p className="mt-4 text-lg text-[#5E5E5A] max-w-xl">
								Whether you need help measuring, choosing styles, or have any
								questions, our experts are ready to assist.
							</p>
						</div>
						<div className="flex flex-wrap items-center gap-x-8 gap-y-4">
							<button
								type="button"
								onClick={() => setOpenCalendly(true)}
								className="group inline-flex items-center gap-2 border-b border-[#141414] pb-1 uppercase tracking-[0.2em] text-xs font-medium"
							>
								Book a free consultation
								<ArrowRight
									aria-hidden="true"
									strokeWidth={1.5}
									className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1"
								/>
							</button>
							<Link
								to="/help/faq"
								className="inline-flex items-center border-b border-[#141414]/30 pb-1 uppercase tracking-[0.2em] text-xs font-normal hover:border-[#141414]"
							>
								Read the FAQ
							</Link>
						</div>
					</div>
				</section>
			</div>

			{/* Calendly Popup Modal */}
			<PopupModal
				url="https://calendly.com/offcutshades"
				rootElement={document.getElementById("root")}
				open={openCalendly}
				onModalClose={() => setOpenCalendly(false)}
			/>
		</div>
	);
};

export default HomeScreen;
